/**
 * Generates a reference number based on landlord and tenant information
 * @param {Object} landlord - The landlord object with id, firstName and lastName
 * @param {Object} tenant - The tenant object with id, firstName and lastName (optional)
 * @param {string} suffix - Optional suffix (e.g., property number)
 * @returns {string} - The generated reference number
 */
export const generateReferenceNumber = (landlord, tenant = null, suffix = '') => {
    if (!landlord) return '';
    
    // Create landlord part: first 3 letters of last name + first 2 chars of ID
    const landlordPart = `${landlord.lastName.substring(0, 3).toUpperCase()}${landlord.id.substring(0, 2)}`;
    
    // Create tenant part if tenant exists
    let tenantPart = '';
    if (tenant) {
      tenantPart = `-${tenant.lastName.substring(0, 3).toUpperCase()}${tenant.id.substring(0, 2)}`;
    }
    
    // Add optional suffix
    const suffixPart = suffix ? `-${suffix}` : '';
    
    return `${landlordPart}${tenantPart}${suffixPart}`;
  };
  
  /**
   * Suggests reference number options based on available data
   * @param {Object} landlord - The landlord object
   * @param {Object} tenant - The tenant object (optional)
   * @param {string} propertyAddress - The property address (optional)
   * @returns {Array} - An array of suggested reference numbers
   */
  export const suggestReferenceNumbers = (landlord, tenant = null, propertyAddress = '') => {
    if (!landlord) return [];
    
    const suggestions = [];
    
    // Basic reference with landlord only
    suggestions.push(generateReferenceNumber(landlord));
    
    // Reference with landlord and tenant if available
    if (tenant) {
      suggestions.push(generateReferenceNumber(landlord, tenant));
    }
    
    // Reference with property info if available
    if (propertyAddress) {
      // Extract street number if possible
      const streetNumberMatch = propertyAddress.match(/^(\d+)/);
      const streetNumber = streetNumberMatch ? streetNumberMatch[1] : '';
      
      if (streetNumber) {
        suggestions.push(generateReferenceNumber(landlord, null, streetNumber));
        
        if (tenant) {
          suggestions.push(generateReferenceNumber(landlord, tenant, streetNumber));
        }
      }
      
      // Use first 3 chars of street name if available
      const streetName = propertyAddress.replace(/^\d+\s+/, '').trim();
      if (streetName && streetName.length >= 3) {
        const streetPrefix = streetName.substring(0, 3).toUpperCase();
        suggestions.push(generateReferenceNumber(landlord, null, streetPrefix));
        
        if (tenant) {
          suggestions.push(generateReferenceNumber(landlord, tenant, streetPrefix));
        }
      }
    }
    
    // Add timestamp-based reference for complete uniqueness
    const timestamp = new Date().getTime().toString().substring(9, 13);
    suggestions.push(generateReferenceNumber(landlord, tenant, timestamp));
    
    // Return unique suggestions
    return [...new Set(suggestions)];
  };